import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Button,
  Typography
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DescriptionIcon from '@mui/icons-material/Description';
import SegmentIcon from '@mui/icons-material/Topic';
import CreateIcon from '@mui/icons-material/Create';
import RateReviewIcon from '@mui/icons-material/RateReview';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import LogoutIcon from '@mui/icons-material/Logout';
import SchoolIcon from '@mui/icons-material/School';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { useAuth } from '../context/AuthContext';
import { links } from '../config/links';

const drawerWidth = 240;

const Navigation = ({ open, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, hasRole } = useAuth();
  
  // Define all menu items with their role requirements
  const allMenuItems = [
    // Dashboard/Generate Exam - dynamic based on user role
    { 
      text: hasRole('STUDENT') ? 'Generate Exam' : 'Dashboard', 
      icon: hasRole('STUDENT') ? <CreateIcon /> : <DashboardIcon />, 
      path: hasRole('STUDENT') ? links.internal.generate : links.internal.home,
      roles: ['STUDENT', 'TUTOR', 'ADMIN'] // All users can see this
    },
    { 
      text: 'Documents', 
      icon: <DescriptionIcon />, 
      path: links.internal.documents,
      roles: ['TUTOR', 'ADMIN']
    },
    { 
      text: 'Content Segments', 
      icon: <SegmentIcon />, 
      path: links.internal.segments,
      roles: ['TUTOR', 'ADMIN']
    },
    { 
      text: 'Generate Exam', 
      icon: <CreateIcon />, 
      path: links.internal.generate,
      roles: ['TUTOR', 'ADMIN'] // Only tutors and admins see this as a separate item
    },
    // Topics & Review removed - functionality moved to Courses & Topics
    { 
      text: 'Complete Review', 
      icon: <AssignmentTurnedInIcon />,
      path: links.internal.completeReviewList,
      roles: ['TUTOR', 'ADMIN']
    },
    { 
      text: 'Courses & Topics', 
      icon: <SchoolIcon />,
      path: '/courses',
      roles: ['TUTOR', 'ADMIN']
    },
  ];

  // Subscription menu items - added separately to control placement
  const subscriptionItems = [
    {
      text: 'Subscription Plans',
      icon: <CreditCardIcon />,
      path: links.internal.subscriptionPlans,
      roles: ['STUDENT', 'TUTOR', 'ADMIN']
    },
    {
      text: 'Manage Subscription',
      icon: <SubscriptionsIcon />,
      path: '/subscription/manage',
      roles: ['STUDENT', 'TUTOR', 'ADMIN']
    }
  ];

  // Filter menu items based on user role
  const menuItems = allMenuItems.filter(item => 
    item.roles.some(role => hasRole(role))
  );
  
  // Filter subscription items based on user role
  const userSubscriptionItems = subscriptionItems.filter(item =>
    item.roles.some(role => hasRole(role))
  );
  
  const handleNavigation = (path) => {
    navigate(path);
    if (onClose) onClose();
  };

  const handleLogout = async () => {
    await logout();
    navigate(links.internal.login);
    if (onClose) onClose();
  };
  
  return (
    <Drawer
      variant="temporary"
      open={open}
      onClose={onClose}
      ModalProps={{
        keepMounted: true, // Better open performance on mobile
      }}
      sx={{
        display: { xs: 'block' },
        '& .MuiDrawer-paper': { 
          boxSizing: 'border-box', 
          width: drawerWidth 
        },
      }}
    >
      <Box sx={{ p: 2 }}>
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <img 
            src="/logo192.png" 
            alt="Logo" 
            style={{ height: 50, width: 'auto' }} 
          />
        </Box>
        {user && (
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Welcome, {user.first_name || user.username}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {user.role}
            </Typography>
          </Box>
        )}
      </Box>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem 
            button 
            key={item.text}
            onClick={() => handleNavigation(item.path)}
            selected={location.pathname === item.path}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText primary={item.text} />
          </ListItem>
        ))}
      </List>
      <Divider />
      {userSubscriptionItems.length > 0 && (
        <>
          <List>
            {userSubscriptionItems.map((item) => (
              <ListItem 
                button 
                key={item.text}
                onClick={() => handleNavigation(item.path)}
                selected={location.pathname === item.path}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItem>
            ))}
          </List>
          <Divider />
        </>
      )}
      <List>
        <ListItem 
          button 
          onClick={() => window.open(links.external.whatsappGroup, '_blank')}
        >
          <ListItemIcon>
            <WhatsAppIcon style={{ color: '#25D366' }} />
          </ListItemIcon>
          <ListItemText 
            primary="Report Issues" 
            secondary="Join WhatsApp group"
            primaryTypographyProps={{ fontSize: '0.9rem' }}
            secondaryTypographyProps={{ fontSize: '0.75rem' }}
          />
        </ListItem>
      </List>
      <Box sx={{ p: 2 }}>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<LogoutIcon />}
          onClick={handleLogout}
          color="secondary"
        >
          Logout
        </Button>
      </Box>
    </Drawer>
  );
};

export default Navigation; 